import React, { useState, useEffect } from 'react';
import { BarChart3, MapPin, Clock, Cloud, Road, AlertTriangle, TrendingUp, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageFrame from '../components/PageFrame';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SafetyStatistics = () => {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedWeather, setSelectedWeather] = useState('');
  const [availableStates, setAvailableStates] = useState([]);

  useEffect(() => {
    fetchInsights();
    fetchStates();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedState, selectedTime, selectedWeather]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/safety-stats/states`);
      setAvailableStates(response.data.available_states);
    } catch (error) {
      console.error("Failed to load states");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedState && selectedState !== 'all') params.append('state', selectedState);
      if (selectedTime && selectedTime !== 'all') params.append('time_period', selectedTime);
      if (selectedWeather && selectedWeather !== 'all') params.append('weather', selectedWeather);
      
      const response = await axios.get(`${API}/safety-stats/data?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/safety-stats/insights`);
      setInsights(response.data);
    } catch (error) {
      console.error("Failed to load insights");
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const getBarWidth = (value, max) => {
    return `${Math.round((value / max) * 100)}%`;
  };

  if (loading && !data) {
    return (
      <PageFrame title="Loading...">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </PageFrame>
    );
  }

  return (
    <>
      <SEO 
        title="Truck Accident Statistics & Safety Calculator"
        description="Interactive truck accident statistics by state, time of day, weather, and road type. Learn when and where truck accidents are most likely to occur."
      />
      
      <PageFrame 
        title="Safety Statistics Calculator"
        subtitle="Explore truck accident data and learn how to stay safe on the road."
      >
        {/* Key Insights */}
        {insights && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {insights.key_insights.map((insight, index) => (
              <Card key={index} className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-orange-500">{insight.value}</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{insight.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{insight.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Select value={selectedState} onValueChange={setSelectedState}>
            <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
              <MapPin className="h-4 w-4 mr-2 text-orange-500" />
              <SelectValue placeholder="Filter by State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {availableStates.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedTime} onValueChange={setSelectedTime}>
            <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
              <Clock className="h-4 w-4 mr-2 text-orange-500" />
              <SelectValue placeholder="Filter by Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Times</SelectItem>
              <SelectItem value="12am-4am">12am - 4am</SelectItem>
              <SelectItem value="4am-8am">4am - 8am</SelectItem>
              <SelectItem value="8am-12pm">8am - 12pm</SelectItem>
              <SelectItem value="12pm-4pm">12pm - 4pm</SelectItem>
              <SelectItem value="4pm-8pm">4pm - 8pm</SelectItem>
              <SelectItem value="8pm-12am">8pm - 12am</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedWeather} onValueChange={setSelectedWeather}>
            <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
              <Cloud className="h-4 w-4 mr-2 text-orange-500" />
              <SelectValue placeholder="Filter by Weather" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Weather</SelectItem>
              <SelectItem value="Clear">Clear</SelectItem>
              <SelectItem value="Rain">Rain</SelectItem>
              <SelectItem value="Snow/Ice">Snow/Ice</SelectItem>
              <SelectItem value="Fog">Fog</SelectItem>
              <SelectItem value="Wind">Wind</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Safety Tips based on filters */}
        {data?.safety_tips && data.safety_tips.length > 0 && (
          <Card className="bg-orange-500/10 border-orange-500/30 mb-8">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-orange-600 dark:text-orange-400 mb-2">Safety Tips for Your Selection</h3>
                  <ul className="space-y-1">
                    {data.safety_tips.slice(0, 5).map((tip, index) => (
                      <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                        <ChevronRight className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {data && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* State Statistics */}
            <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Accidents by State</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(data.data.by_state || {})
                    .filter(([_, val]) => val)
                    .sort((a, b) => (b[1]?.accidents || 0) - (a[1]?.accidents || 0))
                    .slice(0, 10)
                    .map(([state, stats]) => (
                      <div key={state}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 dark:text-gray-300">{state}</span>
                          <span className="text-gray-500">{formatNumber(stats.accidents)} accidents</span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-orange-500 to-red-500"
                            style={{ width: getBarWidth(stats.accidents, 15000) }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* Time of Day Statistics */}
            <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Accidents by Time of Day</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(data.data.by_time || {})
                    .filter(([_, val]) => val)
                    .map(([time, stats]) => (
                      <div key={time}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 dark:text-gray-300">{time}</span>
                          <span className="text-gray-500">{stats.percentage}% of accidents</span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                            style={{ width: `${stats.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* Weather Statistics */}
            <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Cloud className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Weather Impact</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(data.data.by_weather || {})
                    .filter(([_, val]) => val)
                    .sort((a, b) => (b[1]?.accidents_per_100k || 0) - (a[1]?.accidents_per_100k || 0))
                    .map(([weather, stats]) => (
                      <div key={weather}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 dark:text-gray-300">{weather}</span>
                          <span className="text-gray-500">{stats.accidents_per_100k} per 100k miles</span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                            style={{ width: getBarWidth(stats.accidents_per_100k, 160) }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* Causes Statistics */}
            <Card className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Common Causes</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(data.data.by_cause || {})
                    .sort((a, b) => b[1].percentage - a[1].percentage)
                    .map(([cause, stats]) => (
                      <div key={cause}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className={`${stats.preventable ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                            {cause} {stats.preventable && '⚠️'}
                          </span>
                          <span className="text-gray-500">{stats.percentage}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${stats.preventable ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-gray-400 to-gray-500'}`}
                            style={{ width: `${stats.percentage * 4}%` }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
                <p className="text-xs text-gray-500 mt-4">⚠️ = Preventable causes</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Overall Statistics */}
        {data?.data?.overall && (
          <Card className="bg-gray-900 border-gray-700 mt-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-semibold text-white">Annual Statistics Overview</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-500">{formatNumber(data.data.overall.total_accidents_yearly)}</p>
                  <p className="text-sm text-gray-400">Total Accidents</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-500">{formatNumber(data.data.overall.total_fatalities_yearly)}</p>
                  <p className="text-sm text-gray-400">Fatalities</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-500">{formatNumber(data.data.overall.total_injuries_yearly)}</p>
                  <p className="text-sm text-gray-400">Injuries</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-500">{data.data.overall.large_truck_percentage}%</p>
                  <p className="text-sm text-gray-400">Of All Fatal Crashes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-500">${formatNumber(data.data.overall.average_settlement)}</p>
                  <p className="text-sm text-gray-400">Avg. Settlement</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-500">${(data.data.overall.average_fatality_settlement / 1000000).toFixed(1)}M</p>
                  <p className="text-sm text-gray-400">Avg. Fatal Settlement</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-gray-500 text-center mt-8">
          Data is based on FMCSA and NHTSA statistics. Figures are approximate and for educational purposes only.
        </p>
      </PageFrame>
    </>
  );
};

export default SafetyStatistics;
